/**
 * Deposits & Funding Articles — Russian
 */

import type { HelpArticle } from "../../../types";

export const DEPOSITS_ARTICLES_RU: HelpArticle[] = [
  {
    id: "dep-001",
    slug: "how-deposits-work",
    title: "Как работают депозиты",
    description: "Процесс пополнения и конвертации криптовалюты",
    category: "deposits",
    popular: true,
    order: 1,
    keywords: ["депозит", "пополнение", "крипта", "конвертация", "процесс"],
    content: `
# Как работают депозиты

Supabank делает конвертацию крипты в фиат простой и автоматической.

## Общая схема

1. **Создание адреса** — Сгенерируйте уникальный адрес ликвидации
2. **Отправка крипты** — Переведите криптовалюту на адрес
3. **Автоматическое обнаружение** — Мы видим ваш перевод в блокчейне
4. **Конвертация** — Крипта конвертируется в фиат по рыночному курсу
5. **Выплата** — Фиат отправляется на привязанный банковский счёт

## Процесс ликвидации

### Шаг 1: Создание адреса
- Каждый адрес уникален для вас
- Несколько адресов для разных сетей
- Адреса бессрочные

### Шаг 2: Отправка крипты
- Используйте любой кошелёк
- Убедитесь в правильности выбора сети
- Заложите достаточно на газ/комиссии

### Шаг 3: Подтверждение
- Ожидаем подтверждения сети
- Обычно 1–12 подтверждений в зависимости от сети
- Статус: «Ожидание»

### Шаг 4: Конвертация
- Автоматическая по текущему курсу
- Действий от вас не требуется
- Выгодные обменные курсы

### Шаг 5: Зачисление
- Фиат добавляется на баланс
- Автоматическая выплата в банк (если настроено)
- Или ручной вывод

## Сроки

| Сеть | Подтверждений | Обычное время |
|------|---------------|---------------|
| Ethereum | 12 | ~3 минуты |
| Polygon | 128 | ~5 минут |
| Base | 12 | ~30 секунд |
| Solana | 32 | ~15 секунд |

## Важно

⚠️ **Всегда проверяйте сеть** перед отправкой
⚠️ **Минимальный депозит**: зависит от актива и сети (используйте калькулятор)
⚠️ **Максимальный депозит**: зависит от уровня верификации
    `.trim(),
  },
  {
    id: "dep-002",
    slug: "supported-cryptocurrencies",
    title: "Поддерживаемые криптовалюты",
    description: "Какие криптовалюты можно вносить",
    category: "deposits",
    order: 2,
    keywords: ["крипта", "монеты", "токены", "поддерживаемые", "валюты"],
    content: `
# Поддерживаемые криптовалюты

Supabank принимает различные криптовалюты в нескольких сетях.

## Доступные активы

Supabank поддерживает 8 активов для адресов ликвидации: USDC, USDT, PYUSD, USDB, EURC, DAI, USDP и ETH.

### Стейблкоины
| Актив | Сети |
|-------|------|
| USDC | Ethereum, Polygon, Base, Solana, Arbitrum, Stellar |
| USDT | Ethereum, Tron |
| PYUSD | Ethereum, Solana |
| USDB | Base, Solana |
| EURC | Base, Solana |
| DAI | Ethereum |
| USDP | Solana |

### Основные криптовалюты
| Актив | Сети |
|-------|------|
| ETH | Ethereum, Base, Arbitrum, Optimism |

**Примечание:** Bitcoin (BTC) не поддерживается для адресов ликвидации. Доступны только перечисленные выше активы.

## Поддержка сетей

### EVM-сети
- Ethereum (ETH)
- Polygon (MATIC)
- Base
- Arbitrum One
- Optimism
- Avalanche C-Chain

### Не-EVM сети
- Solana
- Tron
- Stellar (скоро)

## Минимальные суммы

Минимумы зависят от актива и сети. Используйте калькулятор в личном кабинете для точных данных.

### Примеры
| Актив | Сеть | Ориентировочный минимум |
|-------|------|------------------------|
| USDC | Base | $10 |
| USDC | Ethereum | от $20 |
| USDT | Ethereum | от $20 |
| ETH | Ethereum | 0,003 ETH (~$10–15) |
| USDC | Solana | $10 |

**Важно:** Минимумы могут меняться в зависимости от состояния сети. Всегда проверяйте калькулятор перед отправкой.

## Неподдерживаемые активы

**Не отправляйте:**
- NFT
- Неподдерживаемые токены
- Активы в неправильной сети

Отправка неподдерживаемых активов может привести к их безвозвратной потере.

## Добавление новых активов

Мы регулярно добавляем новые активы на основе:
- Спроса пользователей
- Доступной ликвидности
- Регуляторных требований
- Технической возможности

Запросить новый актив можно через поддержку.
    `.trim(),
  },
  {
    id: "dep-003",
    slug: "network-chain-selection",
    title: "Выбор сети",
    description: "Как правильно выбрать сеть для депозита",
    category: "deposits",
    order: 3,
    keywords: ["сеть", "блокчейн", "выбор", "chain"],
    content: `
# Выбор сети

Правильный выбор сети — ключ к успешному депозиту.

## Почему сеть важна

- Каждый блокчейн независим
- Один и тот же актив существует в разных сетях
- Неправильная сеть = потеря средств
- Разные комиссии и скорость

## Доступные сети

### Ethereum
- Самая распространённая
- Высокие комиссии за газ
- Максимальная безопасность
- Финализация ~3 минуты

### Polygon
- Низкие комиссии
- Быстрые подтверждения
- EVM-совместимая
- Отлично для стейблкоинов

### Base
- Очень низкие комиссии
- Создана Coinbase
- Быстрые транзакции
- Растущая экосистема

### Solana
- Сверхбыстрая
- Очень низкие комиссии
- Не-EVM
- Другой формат адресов

## Как выбрать сеть

### По комиссиям (от низких к высоким)
1. Solana
2. Base
3. Polygon
4. Arbitrum

### По безопасности
1. Ethereum
2. Base
3. Polygon
4. Arbitrum

### По скорости
1. Solana
2. Base
3. Polygon
4. Arbitrum

## Частые ошибки

**Отправка ETH на адрес Polygon**
Адрес каждой сети уникален. Всегда совпадайте сеть.

**Неправильный стандарт токена**
USDC на Ethereum ≠ USDC на Solana (разные контракты)

**Недостаточно газа**
Убедитесь, что есть нативные токены для комиссий

## Как проверить сеть

В Supabank:
1. Откройте адрес ликвидации
2. Сеть указана явно
3. Отправляйте только совпадающие активы

В кошельке:
1. Проверьте выбранную сеть
2. Убедитесь в контракте токена
3. Подтвердите перед отправкой
    `.trim(),
  },
  {
    id: "dep-004",
    slug: "deposit-processing-times",
    title: "Сроки обработки депозитов",
    description: "Сколько времени занимает обработка депозита",
    category: "deposits",
    order: 4,
    keywords: ["обработка", "время", "ожидание", "подтверждение"],
    content: `
# Сроки обработки депозитов

Узнайте, сколько времени занимает обработка депозита.

## Этапы обработки

### 1. Обнаружение в блокчейне (мгновенно – 1 мин)
- Мы видим вашу транзакцию
- Статус: «Обнаружение»

### 2. Подтверждения (зависит от сети)
- Ожидание подтверждений блокчейна
- Статус: «Подтверждение»

### 3. Конвертация (1–2 минуты)
- Конвертация крипты в фиат
- Статус: «Конвертация»

### 4. Зачисление (мгновенно)
- Фиат добавлен на баланс
- Статус: «Завершено»

## Время подтверждений по сетям

| Сеть | Подтверждений | Обычное время |
|------|---------------|---------------|
| Ethereum | 12 | 2–5 мин |
| Polygon | 128 | 3–8 мин |
| Base | 12 | 30 сек – 1 мин |
| Arbitrum | 12 | 1–3 мин |
| Solana | 32 | 15–30 сек |

## Что влияет на скорость

### Загруженность сети
- Высокий трафик = медленнее
- Проверяйте статус сети
- Пиковое время: 9:00–17:00 UTC

### Цена газа
- Больше газа = быстрее
- Низкий газ может задержать включение
- Некоторые кошельки оптимизируют автоматически

### Сумма
- Крупные суммы могут требовать дополнительной проверки
- Стандартная проверка автоматическая

## Отслеживание депозита

1. Откройте вкладку «Транзакции»
2. Нажмите для подробного статуса
3. Смотрите прогресс подтверждений
4. Показано ориентировочное время завершения

## Задержка депозита

Если депозит занимает больше ожидаемого:
1. Проверьте эксплорер блокчейна
2. Убедитесь, что транзакция подтверждена
3. Проверьте правильность сети
4. Напишите в поддержку при задержке > 1 часа
    `.trim(),
  },
  {
    id: "dep-005",
    slug: "minimum-deposit-amounts",
    title: "Минимальные суммы депозитов",
    description: "Минимальные суммы для криптодепозитов",
    category: "deposits",
    order: 5,
    keywords: ["минимум", "сумма", "депозит", "лимит", "порог"],
    content: `
# Минимальные суммы депозитов

Все депозиты должны соответствовать минимальным порогам для обработки.

## Требования к минимумам

### Переменные минимумы

Минимальные суммы **не фиксированы** — они зависят от:
- **Типа актива** (стейблкоины и ETH имеют разные минимумы)
- **Сети** (Ethereum имеет более высокие минимумы, чем L2-сети)
- **Рыночных условий** (загруженность сети может влиять на минимумы)

**Всегда используйте калькулятор** в личном кабинете для точной суммы по вашему активу и сети.

### Зачем нужны минимумы

- Сетевые комиссии делают мелкие депозиты невыгодными
- Затраты на обработку каждой транзакции
- Регуляторные требования
- Поддержание качества сервиса

## Как рассчитываются минимумы

Минимумы определяются:
1. **Базовый минимум актива** (зависит от типа)
2. **Требования сети** (Ethereum требует больше из-за газа)
3. **Валютная конвертация** (при депозите в не-USD фиат)

### Ориентировочные минимумы по сетям

**Сети с низкими комиссиями (Base, Polygon, Solana):**
- USDC/USDT: ~$10
- ETH: ~0,003 ETH

**Сети с высокими комиссиями (Ethereum mainnet):**
- USDC/USDT: от $20
- ETH: ~0,003 ETH (~$10–15)

**Примечание:** Это ориентировочные значения. Для точных сумм используйте калькулятор.

## Что происходит при сумме ниже минимума?

### Депозиты < $10
- Транзакция обнаружена
- Автоматически не обрабатывается
- Обратитесь в поддержку
- Может быть зачислено за вычетом комиссии

### Пылевые суммы (< $1)
- Могут быть невозвратимы
- Комиссии сети часто превышают сумму
- Избегайте отправки очень мелких сумм

## Объединение депозитов

Нельзя объединять несколько мелких депозитов. Каждая транзакция должна соответствовать минимуму отдельно.

## Советы

Рассчитайте USD-эквивалент перед отправкой
Добавьте запас выше минимума
Проверьте текущие курсы
Учтите сетевые комиссии в сумме
    `.trim(),
  },
  {
    id: "dep-006",
    slug: "failed-deposits-troubleshooting",
    title: "Проблемы с депозитами",
    description: "Что делать, если депозит не прошёл",
    category: "deposits",
    order: 6,
    keywords: ["ошибка", "проблема", "не прошёл", "застрял"],
    content: `
# Проблемы с депозитами

Иногда депозиты проходят не так, как ожидалось. Вот как решить проблемы.

## Частые проблемы

### 1. Неправильная сеть
**Проблема:** Отправлено на правильный адрес, но в другой сети

**Решение:**
- Средства могут быть восстановлены (зависит от ситуации)
- Обратитесь в поддержку с данными транзакции
- Восстановление занимает время и может быть платным

### 2. Ниже минимума
**Проблема:** Депозит меньше минимума $10

**Решение:**
- Транзакция обнаружена, но не обработана
- Обратитесь в поддержку для ручной проверки
- Возможно удержание комиссии

### 3. Неподдерживаемый токен
**Проблема:** Отправлен неподдерживаемый токен

**Решение:**
- Восстановление может быть невозможно
- Немедленно обратитесь в поддержку
- Укажите адрес контракта токена

### 4. Недостаточно подтверждений
**Проблема:** Транзакция подтверждена, но депозит не отображается

**Решение:**
- Дождитесь нужного количества подтверждений
- Проверьте статус сети
- Обычно решается в течение 30 минут

## Порядок действий

### Шаг 1: Проверьте транзакцию
1. Получите хеш транзакции
2. Проверьте в эксплорере блокчейна
3. Убедитесь, что транзакция успешна
4. Запишите: подтверждения, сеть, сумму

### Шаг 2: Проверьте в Supabank
1. Откройте «Транзакции»
2. Найдите ожидающие депозиты
3. Проверьте статус и прогресс
4. Запишите сообщения об ошибках

### Шаг 3: Обратитесь в поддержку
Если депозит не появился через час:
1. Напишите на support@supabank.xyz
2. Укажите:
   - Хеш транзакции
   - Использованную сеть
   - Отправленную сумму
   - Email в Supabank
   - Скриншот транзакции

## Сроки решения

| Проблема | Обычное время |
|----------|---------------|
| Задержка подтверждения | 1–2 часа |
| Ниже минимума | 1–2 рабочих дня |
| Неправильная сеть | 3–7 рабочих дней |
| Неподдерживаемый токен | Индивидуально |

## Профилактика

Дважды проверьте сеть перед отправкой
Внимательно проверьте адрес
Сначала отправьте тестовую сумму для крупных депозитов
Используйте только поддерживаемые активы
    `.trim(),
  },
  {
    id: "dep-007",
    slug: "deposit-confirmations",
    title: "Подтверждения депозитов",
    description: "Что такое подтверждения в блокчейне",
    category: "deposits",
    order: 7,
    keywords: ["подтверждения", "блокчейн", "ожидание", "подтверждено"],
    content: `
# Подтверждения депозитов

Узнайте, что такое подтверждения в блокчейне и почему они важны.

## Что такое подтверждения?

Когда вы отправляете крипту:
1. Транзакция отправляется в сеть
2. Включается в блок (1 подтверждение)
3. Поверх добавляются новые блоки
4. Каждый новый блок = ещё 1 подтверждение

## Зачем нужны подтверждения

- Защита от двойной траты
- Гарантия завершённости транзакции
- Защита от реорганизации блокчейна
- Требование безопасности

## Необходимое количество подтверждений

| Сеть | Подтверждений | Причина |
|------|---------------|---------|
| Ethereum | 12 | Высокий стандарт безопасности |
| Polygon | 128 | Модель безопасности PoS |
| Base | 12 | L2 с безопасностью ETH |
| Arbitrum | 12 | L2 с безопасностью ETH |
| Solana | 32 | Быстрая финализация |
| Tron | 20 | Стандарт сети |

## Хронология подтверждений

### Пример: Ethereum
- 0 подтв.: Транзакция отправлена
- 1 подтв.: В первом блоке (~12 сек)
- 12 подтв.: Полностью подтверждено (~2–3 мин)

### Пример: Solana
- 0 подтв.: Транзакция отправлена
- 1 подтв.: Первый слот (~400 мс)
- 32 подтв.: Полностью подтверждено (~15 сек)

## Отслеживание подтверждений

### В Supabank
1. Откройте «Транзакции»
2. Нажмите на ожидающий депозит
3. Смотрите прогресс-бар подтверждений
4. Показано текущее / требуемое

### В блокчейне
1. Скопируйте хеш транзакции
2. Откройте эксплорер блокчейна
3. Проверьте поле «Подтверждения»

## Зависшие транзакции

Если подтверждения не растут:
- Возможна загруженность сети
- Низкий газ может задерживать включение
- Проверьте страницу статуса сети
- Подождите или обратитесь в поддержку
    `.trim(),
  },
];
